import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { message } from 'antd';
import moment from 'moment';
import { MdBlock } from 'react-icons/md';

import CommunityMessagesAPI from '../../../api/CommunityMessageAPI';
import { SocketAPI } from '../../../api/SocketAPI';
import CustomEllipsisText from '../../../components/CustomEllipsisText/CustomEllipsisText';
import RenderMentionText from '../../../components/RenderMentionText/RenderMentionText';
import {
  setReplyMessageData,
  setTouchedMessageData,
  updateOrDeleteSelectedMessage,
} from '../../../context/communityMessageReducer';
import showAppError from '../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../shared/hooks';
import {
  ICommunityMessage,
  IPollVoteByUser,
  PollOption,
} from '../../../types/communityMessagesTypes';
import { TFileTypes } from '../../../types/communityTypes';
import {
  IPollVoteReqBody,
  SOCKET_ACTION_EVENTS,
} from '../../../types/socketTypes';
import CreatePostMediaRender from '../../CreateCommunityPost/components/CreatePostMediaRender/CreatePostMediaRender';
import CommunityPostPoll from '../CommunityPost/CommunityPostItems/CommunityPostPoll';
import CreatorTag from './CreatorTag';
import PollVotedUserListModal from './PollVotedUserListModal';

type Props = {
  message: ICommunityMessage;
  showReplyCard?: boolean;
  setSelectMessageForInfo?: (messageId: string) => void;
  setIsOnlyReaction?: (isOnlyReaction: boolean) => void;
  setPreSelectedReaction?: (reaction: string) => void;
  setIsReplyInfoOpen?: (isReplyInfoOpen: boolean) => void;
};

interface IUserState {
  page: number;
  users: IPollVoteByUser[];
  loading: boolean;
  hasMore: boolean;
}

const calculateTime = (dateString: string) => {
  const timeDiffInHrs = moment().diff(moment(dateString), 'hours');
  if (timeDiffInHrs > 24) {
    return moment(dateString).format('hh:mm A');
  }
  return `${timeDiffInHrs} hrs`;
};

const MessagesCard: React.FC<Props> = ({
  message: messageItem,
  showReplyCard = true,
  setSelectMessageForInfo,
  setIsOnlyReaction,
  setPreSelectedReaction,
  setIsReplyInfoOpen,
}) => {
  const { hostMetadata } = useAppSelector((state) => state?.app);

  const userDetails = useAppSelector((state) => state.user);
  const nevigate = useAppNavigate();
  const dispatch = useAppDispatch();

  // const { colors } = useTheme();

  const timerRef = useRef<any>(null);

  const intialState: IUserState = useMemo(
    () => ({
      page: 1,
      users: [],
      loading: false,
      hasMore: true,
    }),
    [],
  );

  const [votedOption, setVotedOption] = useState<string | false>(false);
  const [totalVotes, setTotalVotes] = useState(0);
  const [selectedOption, setSelectedOption] = useState('all');
  const [showPollVotedUserList, setShowPollVotedUserList] = useState(false);
  const [userState, setUserState] = useState(intialState);
  const [selectedTotalVotesCount, setSelectedTotalVotesCount] = useState(0);
  // const [isReactionVisible, setIsReactionVisible] = useState(false);

  const fetchUserList = async () => {
    if (userState?.loading) return;
    if (!userState?.hasMore) return;
    try {
      setUserState((prev) => ({
        ...prev,
        loading: true,
      }));
      const query = new URLSearchParams();
      query.append('page', userState.page.toString());
      query.append('pageSize', '10');
      if (selectedOption !== 'all') {
        query.append('options', selectedOption);
      }
      const resp = await CommunityMessagesAPI.getPollVotesInMessage(
        messageItem.room,
        messageItem._id,
        query?.toString() || '',
      );
      if (resp.status === 200) {
        setUserState((prev) => ({
          ...prev,
          users: [...prev?.users, ...resp?.data?.result?.votes],
          loading: false,
          hasMore: resp?.data?.result?.votes?.length === 10,
          page: prev?.page + 1,
        }));
        setSelectedTotalVotesCount(resp?.data?.result?.totalVotes || 0);
      } else {
        showAppError(resp);
        setUserState((prev) => ({
          ...prev,
          loading: false,
          hasMore: false,
        }));
      }
    } catch (error) {
      setUserState((prev) => ({
        ...prev,
        loading: false,
        hasMore: false,
      }));
      showAppError(error);
    }
  };

  const onlongtouch = () => {
    timerRef.current = null;
    dispatch(setTouchedMessageData(messageItem));
  };

  const touchstart = (e: any) => {
    e.stopPropagation();
    if (!timerRef?.current) {
      timerRef.current = setTimeout(onlongtouch, 500);
    }
  };

  const touchend = () => {
    //stops short touches from firing the event
    if (timerRef) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleReplyClick = () => {
    dispatch(setReplyMessageData(messageItem));
  };

  const fetchVotedOption = async () => {
    try {
      const query = new URLSearchParams();
      query.append('votedBy', userDetails?.id as string);
      query.append('page', '1');
      query.append('limit', '1');
      const resp = await CommunityMessagesAPI.getPollVotesInMessage(
        messageItem.room,
        messageItem._id,
        query?.toString() || '',
      );
      if (resp.status === 200) {
        setVotedOption(resp?.data?.result?.votes?.[0]?.pollOption || false);
        setTotalVotes(resp?.data?.result?.totalVotes || 0);
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const undoVoteOrVote = async (
    optionId?: string,
    isUndoVote: boolean = false,
  ) => {
    const reqBody: IPollVoteReqBody = {
      room: messageItem.room,
      messageId: messageItem._id,
      poll: messageItem.poll as string,
      optionId: optionId as string,
    };
    if (isUndoVote) {
      reqBody.undo = true;
    }
    SocketAPI.emit(
      SOCKET_ACTION_EVENTS.ROOM_MESSAGE_POLL_VOTE,
      reqBody,
      (resp: any) => {
        if (resp?.status === 'success') {
          dispatch(
            updateOrDeleteSelectedMessage(resp.result, false, () =>
              message.success('Your vote has been submitted successfully'),
            ),
          );
        } else if (resp?.status === 'error') {
          message.error(
            typeof resp?.result === 'string'
              ? resp?.result
              : resp?.messageItem || 'Failed to submit your vote',
          );
        }
      },
    );
  };

  const handleClickOnReaction = (emoji: string) => {
    if (setSelectMessageForInfo) {
      setSelectMessageForInfo(messageItem?._id);
      setIsOnlyReaction?.(true);
      setPreSelectedReaction?.(emoji);
      setIsReplyInfoOpen?.(true);
    }
  };

  useEffect(() => {
    if (messageItem?.isDeleted) return;
    const element = document.getElementById(messageItem?._id);
    if (element) {
      element.addEventListener('touchstart', touchstart, false);
      element.addEventListener('touchend', touchend, false);
      element.addEventListener('touchmove', touchend, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageItem?._id]);

  useEffect(() => {
    if (messageItem?.pollMetadata && messageItem?.poll) {
      fetchVotedOption();
    }
    return () => {
      const element = document.getElementById(messageItem?._id);
      if (element) {
        element.removeEventListener('touchstart', touchstart, false);
        element.removeEventListener('touchend', touchend, false);
        element.removeEventListener('touchmove', touchend, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageItem]);

  useEffect(() => {
    if (selectedOption) {
      setUserState(intialState);
    }
  }, [intialState, selectedOption]);

  if (messageItem?.isDeleted) {
    return (
      <div className="chat-card">
        <div className="chat-card_avatar">
          <img src={messageItem?.sender?.profilePicUrl} />
        </div>
        <div className="chat-card_content">
          <div className="chat-card_content_text">
            <p className="chat-card_content_text_name">
              {messageItem?.sender?.name}
            </p>
            {hostMetadata?.creator?._id === messageItem?.sender?._id ? (
              <CreatorTag />
            ) : null}
            <p className="chat-card_content_text_time">
              {moment(messageItem?.createdAt).format('hh:mm A')}{' '}
              {messageItem?.edited ? <span>(Edited)</span> : null}
            </p>
          </div>
          <div className="chat-card_content_text">
            <MdBlock />
            <p
              className="chat-card_avatar_text"
              style={{ fontSize: 12, margin: 0 }}>
              This message was deleted.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="chat-card"
      key={messageItem?._id}
      id={messageItem?._id}
      style={{ zIndex: 0 }}>
      {/* avatar */}
      <div className="chat-card_avatar">
        <img src={messageItem?.sender?.profilePicUrl} />
      </div>
      <div className="chat-card_content">
        {/* sender name and time */}
        <div className="chat-card_content_text">
          <p className="chat-card_content_text_name">
            {messageItem?.sender?.name}
          </p>
          {hostMetadata?.creator?._id === messageItem?.sender?._id ? (
            <CreatorTag />
          ) : null}
          <p className="chat-card_content_text_time">
            {moment(messageItem?.createdAt).format('hh:mm A')}{' '}
            {messageItem?.edited ? <span>(Edited)</span> : null}
          </p>
        </div>
        {/* messageItem content */}
        {messageItem?.message.trim() ? (
          <div className="chat-card_content_message">
            <p>
              <CustomEllipsisText
                text={messageItem?.message}
                charLimit={129}
                customDisplay={(text) => <RenderMentionText string={text} />}
              />
            </p>
          </div>
        ) : null}
        {messageItem?.pollMetadata ? (
          <CommunityPostPoll
            undoVote={(option) => undoVoteOrVote(option, true)}
            vote={(option) => undoVoteOrVote(option)}
            poll={{
              _id: messageItem?.poll,
              creator: messageItem?.pollMetadata?.creator,
              question: messageItem?.pollMetadata?.question,
              pollOptions: messageItem?.pollMetadata?.pollOptions,
              pollStartAt: messageItem?.pollMetadata?.pollStartAt,
              pollEndAt: messageItem?.pollMetadata?.pollEndAt,
              isResultVisibleToMembers:
                messageItem?.pollMetadata?.isResultVisibleToMembers,
              allowVoteChange: messageItem?.pollMetadata?.allowVoteChange,
            }}
            postedBy={messageItem?.sender}
            pollVotesPerOption={
              messageItem.pollMetadata.pollVotesPerOption || {}
            }
            totalPollVotes={totalVotes}
            votedOption={votedOption}
            setSelectedOption={setSelectedOption}
            setShowPollVotedUserList={setShowPollVotedUserList}
            style={{
              width: 'calc(100% - 40px)',
            }}
          />
        ) : null}
        {messageItem.assets?.length ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
            {messageItem.assets.map((each) => (
              <CreatePostMediaRender
                key={each?.asset}
                files={[
                  {
                    url: each?.contentUrl,
                    type: each?.fileType as TFileTypes,
                    name: each?.fileName,
                    fileSize: each?.fileSize,
                  },
                ]}
                isMessageRender
              />
            ))}
          </div>
        ) : null}
        {/* Reaction and reply */}
        <div className="chat-card_content_action">
          {/* Reaction */}
          {Object.values(messageItem?.reactions || {}).some(
            (each) => each > 0,
          ) ? (
            <div className="chat-card_content_action_reactions">
              {Object.entries(messageItem?.reactions || {})
                .filter(([, reactionCount]) => reactionCount > 0)
                .map(([emoji, reactionCount]) => (
                  <div
                    className="chat-card_content_action_reactions_emoji"
                    onClick={() => handleClickOnReaction(emoji)}>
                    {emoji}{' '}
                    {reactionCount > 99 ? `${reactionCount}+` : reactionCount}
                  </div>
                ))}
            </div>
          ) : null}
          {/* replay user avatar and reply count */}
          {messageItem?.threadRootMetadata?.firstFiveRepliers?.length &&
          showReplyCard ? (
            <div className="chat-card_content_action_replay">
              <div className="chat-card_content_action_replay_">
                {messageItem?.threadRootMetadata?.firstFiveRepliers.map(
                  (eachUser) => (
                    <div className="chat-card_content_action_replay__avtar">
                      <img src={eachUser?.profilePicUrl} />
                    </div>
                  ),
                )}
                {messageItem?.threadRootMetadata?.totalReplies > 2 ? (
                  <div className="chat-card_content_action_replay__avtar chat-card_content_action_replay__count">
                    +
                    {messageItem?.threadRootMetadata?.totalReplies -
                      messageItem?.threadRootMetadata?.firstFiveRepliers
                        ?.length}
                  </div>
                ) : null}
              </div>
              <div className="chat-card_content_action_replay_button">
                <button onClick={handleReplyClick}>
                  {messageItem?.threadRootMetadata?.totalReplies}{' '}
                  {messageItem?.threadRootMetadata?.totalReplies === 1
                    ? 'reply'
                    : 'replies'}
                </button>
                <p>
                  Last reply{' '}
                  {calculateTime(
                    messageItem?.threadRootMetadata?.lastRepliedAt,
                  )}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <PollVotedUserListModal
        showModal={showPollVotedUserList}
        closeModal={() => {
          nevigate.goBack();
          setShowPollVotedUserList(false);
        }}
        question={messageItem?.pollMetadata?.question as string}
        pollOptions={messageItem?.pollMetadata?.pollOptions as PollOption[]}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        userList={userState?.users}
        totalPollVotes={selectedTotalVotesCount}
        fetchUserList={fetchUserList}
        hasMore={userState?.hasMore}
      />
    </div>
  );
};

export default memo(MessagesCard);
